import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown, Layers, LayoutDashboard, Building2, Compass, ArrowLeftRight, Route, UserRound, Wifi, ShieldCheck, MapPin } from "lucide-react";
import { NAV_GROUPS } from "./data";
import { Logo } from "./Logo";
import { ease } from "./motion";
import { getLenis, scrollToId } from "@/lib/scroll";

const ICONS = {
  services: Layers, unifi: LayoutDashboard, enterprise: Building2,
  manifesto: Compass, results: ArrowLeftRight, process: Route, about: UserRound,
  "wifi-planner": Wifi, "health-check": ShieldCheck, coverage: MapPin,
};
const ALL_IDS = [...NAV_GROUPS.flatMap((g) => g.items.map((i) => i.id)), "contact"];

// Which section is currently in the middle of the screen, so the menu can highlight it.
const useActiveSection = () => {
  const [active, setActive] = useState(null);
  useEffect(() => {
    const els = ALL_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    els.forEach((el) => io.observe(el));
    const top = () => window.scrollY < 300 && setActive(null);
    window.addEventListener("scroll", top, { passive: true });
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", top);
    };
  }, []);
  return active;
};
const groupOf = (id) => NAV_GROUPS.find((g) => g.items.some((i) => i.id === id))?.id;

const Dropdown = ({ group, active, go, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 10, filter: "blur(4px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    exit={{ opacity: 0, y: 6, filter: "blur(4px)" }}
    transition={{ duration: 0.28, ease }}
    style={{ x: "-50%" }}
    className="absolute left-1/2 top-full z-50 w-[380px] pt-4"
    data-testid={`nav-dropdown-${group.id}`}
    onMouseLeave={onClose}
  >
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0B0F17]/95 p-2 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(0,240,255,0.04)] backdrop-blur-xl">
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" aria-hidden />
      <p className="px-3 pt-2 pb-1 font-mono text-[9px] tracking-[0.3em] text-slate-500">LNS://{group.id}</p>
      {group.items.map((it) => {
        const Icon = ICONS[it.id];
        const on = active === it.id;
        return (
          <button key={it.id} onClick={() => go(it.id)} data-testid={`nav-link-${it.id}`} className={`group relative flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-white/[0.04] ${on ? "bg-cyan-300/[0.06]" : ""}`}>
            <span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg border transition-colors ${on ? "border-cyan-300/60 text-cyan-300" : "border-white/10 text-slate-400 group-hover:border-cyan-300/50 group-hover:text-cyan-300"}`}>
              <Icon size={16} strokeWidth={1.6} />
            </span>
            <span className="min-w-0">
              <span className={`block text-sm font-semibold ${on ? "text-cyan-200" : "text-slate-100"}`}>{it.label}</span>
              <span className="mt-0.5 block text-xs text-slate-500">{it.desc}</span>
            </span>
            <ArrowUpRight size={14} className="ml-auto mt-1 shrink-0 text-slate-600 opacity-0 transition-[opacity,transform] duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-cyan-300 group-hover:opacity-100" />
          </button>
        );
      })}
    </div>
  </motion.div>
);

export const Nav = ({ ready }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(null);
  const active = useActiveSection();
  const navRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  // Freeze page scroll while the full-screen phone menu is open.
  useEffect(() => {
    if (!open) return;
    getLenis()?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      getLenis()?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  // Desktop dropdowns close on outside click or Escape.
  useEffect(() => {
    if (!drop) return;
    const click = (e) => !navRef.current?.contains(e.target) && setDrop(null);
    const key = (e) => e.key === "Escape" && setDrop(null);
    document.addEventListener("pointerdown", click);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", click);
      document.removeEventListener("keydown", key);
    };
  }, [drop]);

  const go = (id) => {
    const wasOpen = open;
    setOpen(false);
    setDrop(null);
    // Let the menu close (and page scrolling resume) before gliding to the section.
    if (wasOpen) setTimeout(() => scrollToId(id), 60);
    else scrollToId(id);
  };
  const hoverOpen = (id) => {
    clearTimeout(closeTimer.current);
    setDrop(id);
  };
  const hoverClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setDrop(null), 180);
  };
  const activeGroup = groupOf(active);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={ready ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 1, ease, delay: 0.4 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled || open ? "bg-void/75 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 flex items-center justify-between">
        <button onClick={() => go("top")} data-testid="nav-logo" aria-label="Lunar Network Solutions home" className="text-left">
          <Logo />
        </button>
        <nav ref={navRef} className="hidden lg:flex items-center gap-1" aria-label="Main">
          {NAV_GROUPS.map((g, i) => {
            const isOpen = drop === g.id;
            const on = activeGroup === g.id;
            return (
              <div key={g.id} className="relative" onMouseEnter={() => hoverOpen(g.id)} onMouseLeave={hoverClose}>
                <button
                  onClick={() => setDrop(isOpen ? null : g.id)}
                  aria-expanded={isOpen}
                  aria-haspopup="true"
                  data-testid={`nav-group-${g.id}`}
                  className={`relative flex items-center gap-1.5 rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${isOpen || on ? "text-white" : "text-slate-400 hover:text-white"}`}
                >
                  <span className="text-cyan-300/70">0{i + 1}</span>
                  {g.label}
                  <ChevronDown size={13} className={`transition-transform duration-300 ${isOpen ? "rotate-180 text-cyan-300" : ""}`} />
                  {on && <motion.span layoutId="nav-active" className="absolute inset-x-4 -bottom-0.5 h-px bg-cyan-300 shadow-[0_0_10px_#00F0FF]" transition={{ duration: 0.5, ease }} />}
                </button>
                <AnimatePresence>{isOpen && <Dropdown group={g} active={active} go={go} onClose={hoverClose} />}</AnimatePresence>
              </div>
            );
          })}
          <button onClick={() => go("contact")} data-testid="nav-link-contact" className={`relative rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${active === "contact" ? "text-white" : "text-slate-400 hover:text-white"}`}>
            <span className="text-cyan-300/70 mr-1.5">04</span>Contact
            {active === "contact" && <motion.span layoutId="nav-active" className="absolute inset-x-4 -bottom-0.5 h-px bg-cyan-300 shadow-[0_0_10px_#00F0FF]" transition={{ duration: 0.5, ease }} />}
          </button>
        </nav>
        <div className="flex items-center gap-4">
          <span className="hidden md:flex lg:hidden xl:flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-emerald-400/90" data-testid="nav-status">
            <span className="relative flex h-2 w-2"><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" /><span className="relative h-2 w-2 rounded-full bg-emerald-400" /></span>
            ALL SYSTEMS UP
          </span>
          <button onClick={() => go("contact")} data-testid="nav-cta-button" className="hidden sm:inline-flex items-center gap-2 rounded-full bg-slate-100 text-void px-5 py-2.5 text-sm font-semibold hover:bg-cyan-300 transition-colors">
            Book a consult <ArrowUpRight size={15} />
          </button>
          <button className="lg:hidden -mr-2 grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-void/60" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.span key={open ? "x" : "m"} initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.25 }}>
                {open ? <X size={20} /> : <Menu size={20} />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease }}
            className="lg:hidden absolute inset-x-0 top-full h-[calc(100svh-4rem)] overflow-y-auto overscroll-contain bg-void border-t border-white/10"
            data-testid="nav-mobile-menu"
            data-lenis-prevent
          >
            <div className="absolute inset-0 grid-bg-tight opacity-60 pointer-events-none" aria-hidden />
            <div className="absolute -right-24 top-10 h-72 w-72 rounded-full bg-cyan-500/10 blur-[90px] pointer-events-none" aria-hidden />
            <nav className="relative px-5 pt-6 pb-10 flex min-h-full flex-col sm:px-8" aria-label="Main">
              <p className="font-mono text-[10px] tracking-[0.3em] text-slate-500">LNS://menu</p>
              <div className="mt-2 flex flex-col gap-7 sm:grid sm:grid-cols-2 sm:gap-x-10">
                {NAV_GROUPS.map((g, gi) => (
                  <motion.div key={g.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease, delay: 0.12 + gi * 0.08 }}>
                    <p className="mt-5 mb-1 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-300/80">
                      <span>0{gi + 1}</span>
                      {g.label}
                      <span className="h-px flex-1 bg-gradient-to-r from-cyan-300/30 to-transparent" />
                    </p>
                    {g.items.map((it) => {
                      const Icon = ICONS[it.id];
                      const on = active === it.id;
                      return (
                        <button key={it.id} onClick={() => go(it.id)} data-testid={`nav-mobile-link-${it.id}`} className="group flex w-full items-center gap-3.5 border-b border-white/[0.07] py-3 text-left">
                          <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border transition-colors ${on ? "border-cyan-300/70 text-cyan-300 bg-cyan-300/10" : "border-white/10 text-slate-400 group-active:text-cyan-300"}`}>
                            <Icon size={16} strokeWidth={1.6} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={`block font-display text-xl font-semibold tracking-tight ${on ? "text-cyan-200" : "text-slate-100"}`}>{it.label}</span>
                            <span className="block truncate text-xs text-slate-500">{it.desc}</span>
                          </span>
                          {on ? <span className="font-mono text-[9px] tracking-[0.2em] text-cyan-300">HERE</span> : <ArrowUpRight size={16} className="shrink-0 text-slate-600 group-active:text-cyan-300" />}
                        </button>
                      );
                    })}
                  </motion.div>
                ))}
              </div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.6 }} className="mt-auto pt-10">
                <button onClick={() => go("contact")} data-testid="nav-mobile-link-contact" className="group inline-flex w-full items-center justify-between gap-3 rounded-full bg-cyan-300 text-void pl-7 pr-2 py-2 font-semibold">
                  Book a free consultation
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-void text-cyan-300"><ArrowUpRight size={18} /></span>
                </button>
                <p className="mt-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-emerald-400/90">
                  <span className="relative flex h-2 w-2"><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" /><span className="relative h-2 w-2 rounded-full bg-emerald-400" /></span>
                  ALL SYSTEMS UP
                </p>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
